import { X } from 'lucide-react';
import { useEffect } from 'react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  type?: 'info' | 'error' | 'success' | 'warning';
}

export default function Modal({ isOpen, onClose, title, children, type = 'info' }: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const typeStyles = {
    info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    error: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    success: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
  };

  const iconColors = {
    info: 'text-blue-600 dark:text-blue-400',
    error: 'text-red-600 dark:text-red-400',
    success: 'text-green-600 dark:text-green-400',
    warning: 'text-amber-600 dark:text-amber-400',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
      <div
        className="relative w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl transform transition-all animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b-2 rounded-t-2xl ${typeStyles[type]}`}>
          <h3 className={`text-xl font-bold ${iconColors[type]}`}>
            {title}
          </h3>
          <button
            onClick={onClose}
            className={`p-2 rounded-full hover:bg-white/50 dark:hover:bg-black/20 transition-colors ${iconColors[type]}`}
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {children}
        </div>

        {/* Footer with OK button */}
        <div className="flex justify-end gap-3 p-6 pt-0">
          <button
            onClick={onClose}
            className={`px-6 py-3 rounded-xl font-semibold transition-all transform hover:scale-105 active:scale-95 shadow-md
              ${type === 'error' ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
              ${type === 'success' ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
              ${type === 'warning' ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
              ${type === 'info' ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}
            `}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
